export const etapas = [
  {
    num: "Etapa 1", color: "teal",
    title: "Estrutura mínima e primeiros programas",
    tags: [".global _start", ".text", "syscall exit", "svc 0", "cmp/branch", "hello world"],
    info: [
      { tag: ".global _start", exp: "Torna o símbolo _start visível ao linker. Sem ela o linker não encontra o ponto de entrada e recusa compilar. Equivale ao main() em C — é onde a execução começa.", code: ".global _start\n_start:\n    // execução começa aqui" },
      { tag: ".text",          exp: "Declara que o que segue é código executável. O SO marca essa região como somente leitura/execução na memória virtual. Instruções fora de .text causam falha de segmentação.", code: ".section .text\n_start:\n    mov x8, #93\n    mov x0, #0\n    svc 0" },
      { tag: "syscall exit",   exp: "Termina o processo devolvendo um código de saída ao SO (0–255). Em ARM64 Linux o número da syscall é 93, carregado em x8. O código em x0 pode ser lido com echo $? no terminal.", code: "mov x8, #93    // número da syscall exit\nmov x0, #5     // código de retorno\nsvc 0          // chama o kernel\n// ./prog ; echo $?   → 5" },
      { tag: "svc 0",          exp: "'Supervisor Call': causa uma exceção que transfere o controle ao kernel com o número da syscall em x8. O imediato 0 é ignorado pelo Linux (usado em outros SOs). Após a syscall o kernel retorna o resultado em x0.", code: "mov x8, #93    // número da syscall em x8\nmov x0, #0     // argumento em x0\nsvc 0          // kernel lê x8, executa\n               // resultado retorna em x0" },
      { tag: "cmp/branch",     exp: "cmp subtrai dois valores e atualiza as flags NZCV sem guardar o resultado. Branches condicionais (blt, bge, beq…) leem essas flags para decidir o desvio. Sem cmp anterior, os branches usam flags de instruções anteriores.", code: "mov x0, #5\nmov x1, #10\ncmp x0, x1     // 5 - 10 → N=1, Z=0\nblt menor       // desvia pois 5 < 10\nb fim\nmenor:\n    // bloco executado" },
      { tag: "hello world",    exp: "Usa a syscall write (nº 64): x0 = file descriptor (1 = stdout), x1 = endereço da string, x2 = tamanho em bytes. O tamanho deve ser calculado manualmente — .ascii não inclui terminador nulo.", code: "mov x0, #1          // stdout\nldr x1, =msg        // endereço da string\nmov x2, #14         // tamanho em bytes\nmov x8, #64         // syscall write\nsvc 0\n\n.section .data\nmsg: .ascii \"Hello World!\\n\"" },
    ],
    exemplos: [
      {
        name: "etapa1.s", desc: "exit com código de retorno 5",
        code: `// etapa1.s — estrutura mínima

.global _start
.section .text
_start:
    mov x8, #93       // syscall exit (ARM64 Linux)
    mov x0, #5        // código de retorno
    svc 0             // chamada ao kernel

// Compilar no Raspberry Pi:
//   gcc -static -nostdlib -g etapa1.s -o etapa1
//   ./etapa1 ; echo $?   → 5`
      },
      {
        name: "etapa1_cmpeb.s", desc: "comparação e branch condicional",
        code: `// etapa1_cmpeb.s — cmp e blt

.global _start
.section .text

_start:
    mov x0, #5          // valor A
    mov x1, #10         // valor B
    mov x2, #0          // resultado padrão = 0 (falso)

    cmp x0, x1          // compara A com B (A - B, só flags)
    blt menor           // se A < B, salta para 'menor'
    b fim               // caso contrário, vai para o fim

menor:
    mov x2, #1          // resultado = 1 (verdadeiro)

fim:
    mov x8, #93         // syscall exit
    mov x0, x2          // código de retorno = resultado
    svc 0               // chamada ao kernel

// echo $?  →  1  (porque 5 < 10)`
      },
      {
        name: "hello world", desc: "syscall write + exit",
        code: `// etapa1_helloworld.s — hello world

.global _start

.section .text
_start:
    mov x0, #1          // stdout (file descriptor 1)
    ldr x1, =msg        // endereço da string
    mov x2, #14         // tamanho em bytes (inclui \\n)
    mov x8, #64         // syscall write (número 64 no ARM64)
    svc 0               // escreve no terminal

    mov x0, #0          // código de retorno
    mov x8, #93         // syscall exit
    svc 0

.section .data
msg:
    .ascii "Hello World!\\n"`
      }
    ]
  },
  {
    num: "Etapa 2", color: "teal",
    title: "Tipos, memória e GDB",
    tags: ["xN/wN", ".word", "ldr/str", "add/sub", "GDB stepi", "NZCV flags"],
    info: [
      { tag: "xN/wN",      exp: "ARM64 tem 31 registradores de propósito geral de 64 bits (x0–x30). wN acessa os 32 bits inferiores do mesmo registrador físico. Escrever em wN zera automaticamente os 32 bits superiores de xN.", code: "mov x0, #0xFFFFFFFF00000001\nmov w0, #5\n// após: x0 = 5 (bits superiores zerados automaticamente)\n\n// usar w para .word (32 bits)\nldr w1, [x0]   // correto\nldr x1, [x0]   // lê 8 bytes — pode pegar dados vizinhos" },
      { tag: ".word",      exp: "Reserva 4 bytes (32 bits) na seção .data com valor inicial. Use .dword para 8 bytes, .byte para 1 byte. Múltiplos valores criam um vetor contíguo na memória.", code: ".section .data\na:  .word 42          // 4 bytes, valor 42\nb:  .word 0           // 4 bytes, valor 0\nv:  .word 10, 20, 30  // vetor de 3 inteiros (12 bytes)" },
      { tag: "ldr/str",    exp: "ldr/str são as únicas formas de acessar memória em ARM64 (arquitetura load/store). ldr x0, =label é uma pseudo-instrução que carrega o endereço. ldr x0, [x1] carrega o conteúdo no endereço em x1.", code: "ldr x0, =a       // x0 = endereço de 'a' (pseudo)\nldr w1, [x0]     // w1 = conteúdo de 'a' (32 bits)\nadd w1, w1, #1   // incrementa\nstr w1, [x0]     // salva de volta em 'a'" },
      { tag: "add/sub",    exp: "Operações sem efeito nas flags NZCV. Use adds/subs para atualizar as flags. O terceiro operando pode ser imediato de 12 bits ou registrador com shift opcional.", code: "add x2, x0, x1         // x2 = x0 + x1\nsub x3, x0, #5         // x3 = x0 - 5\nadd x4, x0, x1, lsl #1 // x4 = x0 + (x1 * 2)\nadds x5, x0, x1        // igual ao add + atualiza NZCV" },
      { tag: "GDB stepi",  exp: "stepi (si) executa uma instrução de máquina por vez. nexti (ni) faz o mesmo mas trata bl como uma instrução única (não entra na função). starti inicia o programa já pausado na primeira instrução.", code: "gdb ./etapa2\n(gdb) starti               // pausa na primeira instrução\n(gdb) si                   // executa 1 instrução\n(gdb) info registers x0 w1 w2 w3\n(gdb) x/w &a               // lê 4 bytes no endereço de 'a'\n(gdb) display/i $pc        // mostra próxima instrução sempre" },
      { tag: "NZCV flags", exp: "N (Negative): bit mais significativo do resultado é 1. Z (Zero): resultado é zero. C (Carry): houve carry em operação sem sinal. V (oVerflow): houve overflow em operação com sinal. Só atualizadas por instruções com sufixo S.", code: "mov x0, #5\nmov x1, #5\nsubs x2, x0, x1  // x2 = 0 → Z=1, N=0, C=1, V=0\nb.eq iguais       // desvia pois Z=1\n\ncmp x0, x1       // equivale a subs xzr, x0, x1" },
    ],
    exemplos: [
      {
        name: "etapa2.s", desc: "ldr de .data, soma, depuração GDB",
        code: `// etapa2.s — leitura de memória e aritmética

.global _start

.section .data
a: .word 5              // variável 'a' (32 bits) = 5
b: .word 3              // variável 'b' (32 bits) = 3

.section .text
_start:
    ldr x0, =a          // x0 = ENDEREÇO de 'a'
    ldr w1, [x0]        // w1 = CONTEÚDO: w1 = 5
                        // (w = 32 bits, compatível com .word)
    ldr x0, =b          // x0 = ENDEREÇO de 'b'
    ldr w2, [x0]        // w2 = CONTEÚDO: w2 = 3

    add w3, w1, w2      // w3 = w1 + w2 = 8

    mov x8, #93
    mov x0, #0
    svc 0

// Depurar no GDB:
//   gdb ./etapa2
//   break _start
//   run
//   stepi              ← executa 1 instrução
//   info registers x0 w1 w2 w3
//   x/w &a             ← ver valor de 'a' na memória`
      }
    ]
  },
  {
    num: "Etapa 3", color: "teal",
    title: "Organização da memória",
    tags: [".data", ".bss", ".equ", "ldr/str", "x vs w regs"],
    info: [
      { tag: ".data",       exp: "Seção de variáveis inicializadas. Os valores existem no arquivo binário e são copiados para a RAM antes da execução. Tem permissão de leitura e escrita em tempo de execução.", code: ".section .data\na:   .word 10\nb:   .word 20\nmsg: .asciz \"hello\"    // string com \\0 automático" },
      { tag: ".bss",        exp: "Block Started by Symbol: variáveis não inicializadas. Não ocupa espaço no arquivo — o SO aloca e zera a memória antes da execução. Use .skip N para reservar N bytes sem valor inicial.", code: ".section .bss\nresultado: .skip 4   // 4 bytes zerados\nbuffer:    .skip 64  // 64 bytes zerados\n\n// .bss não ocupa bytes no binário —\n// o SO cria o espaço em tempo de execução" },
      { tag: ".equ",        exp: "Define uma constante simbólica sem ocupar memória. É uma substituição em tempo de montagem: o assembler troca o nome pelo valor em todo o código. Torna o código legível sem nenhum custo de execução.", code: ".equ STDOUT,    1\n.equ SYS_WRITE, 64\n.equ SYS_EXIT,  93\n\n    mov x8, #SYS_EXIT   // assembler substitui por 93\n    mov x0, #STDOUT     // substitui por 1\n    svc 0" },
      { tag: "ldr/str",     exp: "Para variáveis em .data: ldr x0, =var carrega o endereço (pseudo-instrução); ldr w1, [x0] carrega o conteúdo com tamanho correto. Para gravar: str w1, [x0] escreve no endereço em x0.", code: "ldr x0, =a       // x0 = endereço de 'a'\nldr w1, [x0]     // w1 = valor de 'a'\nadd w1, w1, #1   // incrementa\nstr w1, [x0]     // salva de volta\n// GDB: x/w &a   → mostra valor na memória" },
      { tag: "x vs w regs", exp: "ldr w lê 4 bytes; ldr x lê 8 bytes. Variáveis .word têm 4 bytes — usar ldr x leria os 4 bytes da variável seguinte junto. Sempre usar w para .word e x para .dword ou endereços.", code: ".section .data\na: .word 5    // 4 bytes offset 0\nb: .word 9    // 4 bytes offset 4\n\n    ldr x0, =a\n    ldr w1, [x0]  // correto: w1 = 5\n    ldr x1, [x0]  // errado:  x1 mistura a e b!" },
    ],
    exemplos: [
      {
        name: "etapa3.s", desc: ".bss, str resultado, memória completa",
        code: `// etapa3.s — .data + .bss + ldr + str

.global _start

.section .data
a: .word 5              // variável inicializada
b: .word 3

.section .bss
res: .skip 4            // reserva 4 bytes (sem valor inicial → 0)

.section .text
_start:
    ldr x0, =a          // x0 = endereço de 'a'
    ldr w1, [x0]        // w1 = 5

    ldr x0, =b
    ldr w2, [x0]        // w2 = 3

    add w3, w1, w2      // w3 = 8

    ldr x0, =res        // x0 = endereço de 'res'
    str w3, [x0]        // escreve 8 em 'res' na memória

    mov x8, #93
    mov x0, #0
    svc 0

// Verificar no GDB:
//   x/w &res           → deve mostrar 8`
      }
    ]
  },
  {
    num: "Etapa 4", color: "teal",
    title: "Stack, ponteiros, vetores e strings",
    tags: ["SP/X29/X30", "stp/ldp", "sub sp", "offset", "ldrb", ".ascii"],
    info: [
      { tag: "SP/X29/X30", exp: "SP: aponta para o topo da stack; deve estar alinhado em 16 bytes ao chamar funções. X29 (FP): frame pointer, base do frame atual. X30 (LR): link register, contém o endereço de retorno salvo pelo bl.", code: "// Antes de chamar uma função:\nsub sp, sp, #16       // alinha SP (obrigatório)\n// SP aponta 16 bytes abaixo\n// x29 = base do frame\n// x30 = será sobrescrito pelo próximo bl" },
      { tag: "stp/ldp",    exp: "Store/load pair salva ou restaura dois registradores de 64 bits numa instrução. A forma [sp, -16]! (pre-index) decrementa SP antes do acesso — combina alocação e salvamento. [sp], 16 (post-index) incrementa depois.", code: "// PROLOGUE\nstp x29, x30, [sp, -16]!  // SP -= 16; salva x29 e x30\nmov x29, sp               // frame pointer = SP atual\n\n// corpo da função...\n\n// EPILOGUE\nldp x29, x30, [sp], 16   // restaura; SP += 16\nret" },
      { tag: "sub sp",     exp: "A stack ARM64 cresce para endereços menores: sub sp aloca, add sp libera. O alinhamento de 16 bytes é obrigatório ao chamar outras funções (ABI ARM64). Sempre liberar antes de ret, ou SP ficará corrompido.", code: "sub sp, sp, #32     // aloca 32 bytes\n                    // [sp+0]  → var1 (8 bytes)\n                    // [sp+8]  → var2 (8 bytes)\n                    // [sp+16] → var3 (8 bytes)\n                    // [sp+24] → var4 (8 bytes)\nadd sp, sp, #32     // libera — SEMPRE fazer antes de ret" },
      { tag: "offset",     exp: "Endereçamento por offset acessa posições relativas a um registrador base sem modificá-lo. Essencial para vetores (.word = +4 por elemento) e strings (+1 por byte). Pode ser combinado com shift: [x0, x1, lsl #2].", code: ".section .data\nv: .word 10, 20, 30\n\n    ldr x0, =v\n    ldr w1, [x0]       // v[0] = 10  (offset 0)\n    ldr w2, [x0, #4]   // v[1] = 20  (offset 4)\n    ldr w3, [x0, #8]   // v[2] = 30  (offset 8)" },
      { tag: "ldrb",       exp: "Carrega exatamente 1 byte da memória e zero-extends para 32 bits em wN (bits 8–31 ficam zerados). strb armazena apenas o byte inferior de wN. Indispensável para percorrer strings caractere por caractere.", code: ".section .data\nmsg: .ascii \"ABC\"\n\n    ldr x0, =msg\n    ldrb w1, [x0]      // w1 = 65 ('A')\n    ldrb w2, [x0, #1]  // w2 = 66 ('B')\n    ldrb w3, [x0, #2]  // w3 = 67 ('C')" },
      { tag: ".ascii",     exp: "Armazena bytes da string sem terminador nulo. O tamanho deve ser calculado manualmente para a syscall write. Use .asciz para incluir \\0 automático (compatível com funções C). O símbolo '.' representa o endereço atual e permite calcular o tamanho.", code: ".section .data\nmsg:  .ascii  \"Ola\"    // 3 bytes, sem \\0\nmsg2: .asciz \"Ola\"    // 4 bytes, com \\0\ntam = . - msg          // tam = 3 (calculado pelo assembler)" },
    ],
    exemplos: [
      {
        name: "stack push/pop", desc: "guardar e recuperar valores na stack",
        code: `// etapa4_guardavalorstack.s

.global _start
.section .text

_start:
    mov x0, #10         // valor 1
    mov x1, #20         // valor 2

    sub sp, sp, #16     // reserva 16 bytes (stack cresce para baixo)
    str x0, [sp]        // salva x0 no offset 0
    str x1, [sp, #8]    // salva x1 no offset 8

    ldr x2, [sp]        // recupera: x2 = 10
    ldr x3, [sp, #8]    // recupera: x3 = 20
    add x4, x2, x3      // x4 = 30

    add sp, sp, #16     // libera espaço (SEMPRE restaurar!)

    mov x8, #93
    mov x0, #0
    svc #0`
      },
      {
        name: "frame de função", desc: "prologue/epilogue com stp/ldp",
        code: `// etapa4_frame.s — stack frame básico

.global _start
.section .text

_start:
    bl soma             // chama função (BL = Branch with Link)
                        // salva endereço de retorno em x30
    mov x8, #93
    mov x0, #0
    svc #0

soma:
    stp x29, x30, [sp, -16]!  // PROLOGUE: decrementa SP e
                               // salva frame pointer + link register
    mov x29, sp               // atualiza frame pointer

    mov x0, #5
    mov x1, #7
    add x0, x0, x1            // resultado em x0

    ldp x29, x30, [sp], 16   // EPILOGUE: restaura e incrementa SP
    ret                        // volta para x30 (endereço salvo pelo bl)`
      },
      {
        name: "vetor de inteiros", desc: "acesso com offset em .word",
        code: `// etapa4_vetor.s

.section .data
vetor: .word 10,20,30,40  // 4 × 4 bytes contíguos na memória

.section .text
.global _start
_start:
    ldr x0, =vetor      // x0 = endereço base do vetor

    ldr w1, [x0]        // vetor[0] = 10  (offset 0)
    ldr w2, [x0, #4]    // vetor[1] = 20  (offset 4 bytes)
    ldr w3, [x0, #8]    // vetor[2] = 30  (offset 8 bytes)
    ldr w4, [x0, #12]   // vetor[3] = 40  (offset 12 bytes)

    add w5, w1, w2      // soma parcial
    add w5, w5, w3
    add w5, w5, w4      // w5 = 100

    mov x8, #93
    mov x0, #0
    svc #0`
      },
      {
        name: "string byte a byte", desc: "ldrb para acessar 8 bits",
        code: `// etapa4_string.s

.section .data
msg: .ascii "ABC"       // sem terminador nulo (\\0)

.section .text
.global _start
_start:
    ldr x0, =msg        // x0 = endereço de 'A'

    ldrb w1, [x0]       // w1 = 65 ('A' em ASCII)
    ldrb w2, [x0, #1]   // w2 = 66 ('B')
    ldrb w3, [x0, #2]   // w3 = 67 ('C')

    // ldrb carrega só 1 byte (8 bits), zero-extends para 32 bits

    mov x8, #93
    mov x0, #0
    svc #0`
      }
    ]
  },
  {
    num: "Etapa 5", color: "purple",
    title: "Chamadas de função",
    tags: ["bl/ret", "x30 (LR)", "stp/ldp", "vars locais", "chamadas aninhadas"],
    info: [
      { tag: "bl/ret",           exp: "bl (Branch with Link): salta para a função e salva PC+4 (endereço da instrução seguinte) em x30. ret é equivalente a br x30 — desvia incondicionalmente para o endereço em x30.", code: "_start:\n    mov x0, #5\n    mov x1, #3\n    bl soma          // x30 = endereço da próxima linha\n    // x0 = retorno da função\n    mov x8, #93\n    svc 0\n\nsoma:\n    add x0, x0, x1  // resultado em x0\n    ret              // volta para x30" },
      { tag: "x30 (LR)",         exp: "Link Register: contém o endereço de retorno. Se a função chamar outra com bl, x30 é sobrescrito — o endereço de retorno original se perde. Por isso funções não-folha devem salvar x30 na stack antes de qualquer bl.", code: "funcao_a:\n    stp x29, x30, [sp, -16]!  // salva x30 ANTES do bl\n    bl funcao_b               // x30 sobrescrito aqui\n    ldp x29, x30, [sp], 16   // restaura x30 original\n    ret                       // retorna corretamente" },
      { tag: "stp/ldp",          exp: "O prologue standard salva x29 (FP) e x30 (LR) na stack; o epilogue os restaura na ordem inversa. Obrigatório para funções que chamam outras (non-leaf). Funções folha (sem bl) podem omitir se não modificarem x30.", code: "minha_func:\n    stp x29, x30, [sp, -16]!  // PROLOGUE\n    mov x29, sp               // frame pointer\n\n    // corpo...\n\n    ldp x29, x30, [sp], 16    // EPILOGUE\n    ret" },
      { tag: "vars locais",      exp: "Variáveis locais ficam na stack abaixo do frame pointer. O prologue aloca o espaço (sub sp); cada variável tem um offset negativo fixo de x29. Acessadas com str/ldr usando esse offset.", code: "func:\n    stp x29, x30, [sp, -32]!  // aloca 32 bytes\n    mov x29, sp\n\n    mov x0, #42\n    str x0, [x29, #-8]   // salva var local\n    // ...\n    ldr x1, [x29, #-8]   // recupera var local\n\n    ldp x29, x30, [sp], 32\n    ret" },
      { tag: "chamadas aninhadas", exp: "A → B → C: cada função que chama outra deve salvar x30 antes de bl. Se B não salvar, quando C retornar para B e B fizer ret, usará o x30 sobrescrito e o programa trava ou causa segfault.", code: "func_a:\n    stp x29, x30, [sp, -16]!  // salva ret de A\n    bl func_b\n    ldp x29, x30, [sp], 16\n    ret\n\nfunc_b:\n    stp x29, x30, [sp, -16]!  // salva ret de B\n    bl func_c\n    ldp x29, x30, [sp], 16\n    ret\n\nfunc_c:\n    // função folha: não precisa salvar x30\n    ret" },
    ],
    exemplos: [
      {
        name: "etapa5.s", desc: "soma com variável local na stack",
        code: `// etapa5.s — função com variável local

.global _start
.section .text

_start:
    mov x0, #8          // argumento 1
    mov x1, #12         // argumento 2
    bl soma             // chama soma(8, 12)
    // retorno em x0
    mov x8, #93
    svc #0

// ── Convenção ARM64 ────────────────────────
// Argumentos:  x0–x7
// Retorno:     x0
// Temporários: x0–x18  (não precisam ser preservados)
// Preservados: x19–x28 (devem ser restaurados)
// x29 = frame pointer, x30 = link register
// ──────────────────────────────────────────

soma:
    stp x29, x30, [sp, -16]!    // prologue: salva FP e LR
    mov x29, sp                  // novo frame pointer

    sub sp, sp, #16              // reserva espaço para var local

    add x2, x0, x1               // x2 = a + b

    str x2, [x29, #-8]           // salva em variável local
    ldr x0, [x29, #-8]           // carrega para x0 (valor de retorno)

    add sp, sp, #16              // libera var local
    ldp x29, x30, [sp], 16      // epilogue: restaura FP e LR
    ret`
      }
    ]
  },
  {
    num: "Etapa 6", color: "purple",
    title: "Operações aritméticas inteiras",
    tags: ["add/sub/mul", "madd", "sdiv/udiv", "C → Assembly"],
    info: [
      { tag: "add/sub/mul",  exp: "Operações aritméticas básicas entre registradores ou com imediato. mul xd, xn, xm calcula xn × xm com resultado de 64 bits (bits superiores descartados). Versões com sufixo S (adds, subs) atualizam as flags NZCV.", code: "add x2, x0, x1         // x2 = x0 + x1\nsub x3, x0, #5         // x3 = x0 - 5\nmul x4, x0, x1         // x4 = x0 * x1\nneg x5, x0             // x5 = -x0\nadd x6, x0, x1, lsl #1 // x6 = x0 + (x1 * 2)" },
      { tag: "madd",         exp: "Multiply-accumulate: xd = (xn × xm) + xa. Mais eficiente que mul + add separados. msub xd, xn, xm, xa calcula xd = xa − (xn × xm) — é a forma padrão de obter o resto após sdiv.", code: "// resto após divisão:\nsdiv x2, x0, x1        // x2 = x0 / x1 (quociente)\nmsub x3, x2, x1, x0   // x3 = x0 - (x2 * x1) = resto\n\n// produto acumulado:\nmadd x4, x0, x1, x2   // x4 = (x0 * x1) + x2" },
      { tag: "sdiv/udiv",    exp: "sdiv interpreta os bits como complemento de dois: -40 / 8 = -5. udiv interpreta como positivo: os mesmos bits de -40 como unsigned resultam num número enorme. ARM64 não tem instrução de módulo — use msub.", code: "// com sinal\nmov x0, #-40\nmov x1, #8\nsdiv x2, x0, x1    // x2 = -5\nmsub x3, x2, x1, x0 // x3 = -40-(-5*8) = 0\n\n// sem sinal\nmov x0, #40\nudiv x2, x0, x1    // x2 = 5" },
      { tag: "C → Assembly", exp: "gcc -S -O0 arquivo.c gera o Assembly sem otimizações — a melhor forma de ver como cada construção C vira código. -O0 mantém correspondência direta entre linhas C e instruções ARM64.", code: "// C: int r = (a * b) + c;\nmul x0, x0, x1    // x0 = a * b\nadd x0, x0, x2    // x0 = (a*b) + c\n\n// Para ver o Assembly de qualquer código C:\n// gcc -S -O0 prog.c -o prog.s\n// cat prog.s" },
    ],
    exemplos: [
      {
        name: "Etapa6.s", desc: "add, sub, mul, madd, sdiv, udiv",
        code: `// Etapa6.s — aritmética inteira completa

.global _start
.section .text
_start:

    // ADD
    mov x0, #20
    mov x1, #5
    add x2, x0, x1      // x2 = 25

    // SUB
    sub x3, x0, x1      // x3 = 15

    // MUL
    mul x4, x0, x1      // x4 = 100

    // MADD: xd = (xn * xm) + xa
    madd x5, x0, x1, x3 // x5 = (20 * 5) + 15 = 115

    // SDIV — divisão COM sinal (complemento de dois)
    mov x6, #-40
    mov x7, #8
    sdiv x8, x6, x7     // x8 = -40 / 8 = -5

    // UDIV — divisão SEM sinal (interpreta como unsigned)
    mov x9, #40
    mov x10, #8
    udiv x11, x9, x10   // x11 = 5

    // Resto via MSUB: resto = dividendo - (quociente * divisor)
    // msub xr, xq, xd, xn  → xr = xn - (xq * xd)

    mov x0, x5          // retorna 115
    mov x8, #93
    svc #0`
      }
    ]
  },
  {
    num: "Etapa 7", color: "amber",
    title: "Ponto flutuante (FPU)",
    tags: ["S0–S31 (float)", "D0–D31 (double)", "fadd/fsub/fmul/fdiv", "scvtf/fcvtzs", "NaN/∞"],
    info: [
      { tag: "S0–S31 (float)",        exp: "Registradores de 32 bits (float IEEE-754 single precision) da FPU. Fazem parte do mesmo banco físico que D (64 bits) e V (128 bits) — S0 são os 32 bits inferiores de D0. Nunca misturar com instruções inteiras (add/sub).", code: ".section .data\nf: .float 3.14\n\n    ldr x0, =f\n    ldr s0, [x0]      // s0 = 3.14 (32 bits)\n    fadd s1, s0, s0   // s1 = 6.28\n    // NÃO usar: add s1, s0, s0  (instrução inteira!)" },
      { tag: "D0–D31 (double)",       exp: "Registradores de 64 bits (double IEEE-754 double precision). D0 e S0 compartilham o mesmo registrador físico — D0 acessa todos os 64 bits. Operações entre S e D não são diretas; converta primeiro com fcvt.", code: ".section .data\na: .double 5.5\nb: .double 2.0\n\n    ldr x0, =a\n    ldr d0, [x0]    // d0 = 5.5\n    ldr x1, =b\n    ldr d1, [x1]    // d1 = 2.0\n    fadd d2, d0, d1 // d2 = 7.5" },
      { tag: "fadd/fsub/fmul/fdiv",   exp: "Operações de ponto flutuante que trabalham exclusivamente com registradores FP (sN/dN). fdiv não gera exceção por divisão por zero — retorna ±Infinity conforme IEEE-754. Sintaxe idêntica às inteiras, mas com prefixo f.", code: "ldr d0, [x0]     // carrega 'a'\nldr d1, [x1]     // carrega 'b'\n\nfadd d2, d0, d1  // d2 = a + b\nfsub d3, d0, d1  // d3 = a - b\nfmul d4, d0, d1  // d4 = a * b\nfdiv d5, d0, d1  // d5 = a / b (Inf se d1=0.0)" },
      { tag: "scvtf/fcvtzs",          exp: "scvtf (Signed Convert To Float): converte inteiro (xN/wN) para FP (dN/sN). fcvtzs (Float Convert To Zero Signed): converte FP para inteiro truncando em direção a zero, como o cast (int) em C.", code: "mov x0, #10\nscvtf d0, x0     // d0 = 10.0  (int → double)\n\nmov x1, #3\nscvtf d1, x1     // d1 = 3.0\nfdiv d2, d0, d1  // d2 = 3.333...\nfcvtzs x2, d2    // x2 = 3  (trunca, não arredonda)" },
      { tag: "NaN/∞",                 exp: "IEEE-754: dividir não-zero por 0.0 = ±Infinity; dividir 0.0 por 0.0 = NaN. Nenhuma exceção é gerada por padrão. NaN se propaga: qualquer operação aritmética com NaN retorna NaN. Use fcmp + b.vs para detectar NaN.", code: "// 5.0 / 0.0 = +Infinity\nfdiv d2, d0, d1   // d0=5.0, d1=0.0\n\n// 0.0 / 0.0 = NaN\nfdiv d3, d1, d1   // d1=0.0\n\n// NaN + 5.0 = NaN (propaga)\nfadd d4, d3, d0\n\n// verificar no GDB:\n// p $d2   → inf\n// p $d3   → nan" },
    ],
    exemplos: [
      {
        name: "etapa7a.s", desc: "operações FP: fadd/fsub/fmul/fdiv",
        code: `// Etapa7a.s — operações de ponto flutuante

.global _start

.section .data
a: .double 5.5          // double 64 bits
b: .double 2.0

.section .text
_start:
    // Carregar da memória para registradores FP
    ldr x0, =a
    ldr d0, [x0]        // d0 = 5.5  (D = double 64 bits)

    ldr x1, =b
    ldr d1, [x1]        // d1 = 2.0

    fadd d2, d0, d1     // d2 = 7.5
    fsub d3, d0, d1     // d3 = 3.5
    fmul d4, d0, d1     // d4 = 11.0
    fdiv d5, d0, d1     // d5 = 2.75

    // Converter float → inteiro para retornar ao SO
    fcvtzs x0, d2       // x0 = 7  (trunca 7.5)

    mov x8, #93
    svc #0

// GDB para FP:
//   info registers d0 d1 d2
//   p $d0               → (double) 5.5
//   info float`
      },
      {
        name: "etapa7b.s", desc: "casos de borda: divisão por zero, NaN",
        code: `// Etapa7b.s — casos de borda no IEEE-754

.global _start

.section .data
a:    .double 5.0
zero: .double 0.0

.section .text
_start:
    ldr x0, =a
    ldr d0, [x0]        // d0 = 5.0

    ldr x1, =zero
    ldr d1, [x1]        // d1 = 0.0

    // Caso 1: 5.0 / 0.0 = +Infinity  (não gera exceção!)
    fdiv d2, d0, d1

    // Caso 2: 0.0 / 0.0 = NaN
    fdiv d3, d1, d1

    // Caso 3: NaN + 5.0 = NaN  (NaN se propaga)
    fadd d4, d3, d0

    // IEEE-754: qualquer operação com NaN → NaN
    // Verificar no GDB:
    //   p $d2    →  inf
    //   p $d3    →  nan
    //   p $d4    →  nan

    fcvtzs x0, d2       // infinito → valor máximo de int
    mov x8, #93
    svc #0`
      }
    ]
  },
  {
    num: "Etapa 8", color: "coral",
    title: "Condicionais e loops",
    tags: ["NZCV", "cmp", "b.eq/ne/lt/gt/ge/le", "if-else", "while", "do-while"],
    info: [
      { tag: "NZCV",               exp: "4 flags no PSTATE: N (Negative, bit 31/63 do resultado é 1), Z (Zero, resultado é zero), C (Carry, overflow sem sinal), V (oVerflow, overflow com sinal). Só instruções com sufixo S (adds, subs, ands, cmp, tst) as atualizam.", code: "// adds atualiza flags E guarda resultado\nadds x2, x0, x1  // x2 = x0+x1; NZCV atualizado\n\n// cmp = subs xzr (atualiza, descarta resultado)\ncmp x0, #10      // x0 - 10 → atualiza NZCV\n\n// tst = ands xzr (testa bits, descarta resultado)\ntst x0, #0b111   // testa 3 bits inferiores" },
      { tag: "cmp",                exp: "cmp é uma pseudo-instrução — o assembler gera subs xzr, xn, xm, descartando o resultado mas atualizando NZCV. cmn faz o mesmo com adição. Os operandos não são modificados.", code: "cmp x0, #10        // x0 - 10 → atualiza flags\nb.eq igual         // Z=1 se x0 == 10\nb.lt menor         // N≠V se x0 < 10 (signed)\nb.lo menor_uns     // C=0 se x0 < 10 (unsigned)\n\n// Equivalente ao cmp:\nsubs xzr, x0, #10  // descarta resultado, mantém flags" },
      { tag: "b.eq/ne/lt/gt/ge/le", exp: "Branches condicionais para comparações com sinal (signed). Para unsigned use: b.lo (menor), b.hi (maior), b.hs (≥), b.ls (≤). b.eq desvia se Z=1; b.lt desvia se N≠V.", code: "// Signed (complemento de dois)\ncmp x0, x1\nb.eq igual      // x0 == x1  (Z=1)\nb.ne diferente  // x0 != x1  (Z=0)\nb.lt menor      // x0 < x1   (N≠V)\nb.ge maior_ig   // x0 >= x1  (N=V)\n\n// Unsigned\nb.lo abaixo     // x0 < x1   (C=0)\nb.hi acima      // x0 > x1   (C=1, Z=0)" },
      { tag: "if-else",            exp: "Padrão: cmp + branch que desvia para o else se a condição for falsa. O corpo do if executa em sequência; b incondicional pula o else. O else fica logo após o branch.", code: "// if (x < 10) x++; else x--;\n    ldr x0, =x\n    ldr w1, [x0]\n    cmp w1, #10\n    b.ge else        // se x >= 10 → else\nif:\n    add w1, w1, #1   // x++\n    b fim\nelse:\n    sub w1, w1, #1   // x--\nfim:\n    str w1, [x0]" },
      { tag: "while",              exp: "O teste fica no início do loop. Se falsa na primeira vez, o corpo nunca executa. O branch de repetição (b loop) fica no fim. Gera um branch extra na entrada comparado ao do-while.", code: "// while (i <= 5) soma += i;\n    mov w0, #0    // soma = 0\n    mov w1, #1    // i = 1\nloop:\n    cmp w1, #5\n    b.gt fim      // i > 5 → sai\n    add w0, w0, w1\n    add w1, w1, #1\n    b loop\nfim:" },
      { tag: "do-while",           exp: "O corpo executa primeiro, sem teste inicial. O teste e o branch de repetição ficam no fim. Garante pelo menos uma execução e é mais eficiente que while quando a condição é quase sempre verdadeira na primeira vez.", code: "// do { soma += i; i++; } while (i <= 5);\n    mov w0, #0    // soma = 0\n    mov w1, #1    // i = 1\nloop:\n    add w0, w0, w1   // corpo executa primeiro\n    add w1, w1, #1\n    cmp w1, #5\n    b.le loop        // repete se i <= 5" },
    ],
    exemplos: [
      {
        name: "etapa8a.s", desc: "if/else: x++ ou x-- dependendo de comparação",
        code: `// etapa8a.s — if / else

.section .data
x: .word 8              // testa com 8 (< 10 → vai para if)

.section .text
.global _start
_start:
    ldr x0, =x
    ldr w1, [x0]        // w1 = x = 8

    cmp w1, #10         // subtrai 8 - 10, ajusta flags NZCV
    b.ge else           // se x >= 10 → pula para else

if:                     // x < 10 → executa este bloco
    add w1, w1, #1      // x++ → w1 = 9
    b fim

else:                   // x >= 10 → executa este bloco
    sub w1, w1, #1      // x--

fim:
    str w1, [x0]        // salva novo valor de x na memória

    mov x8, #93
    mov x0, #0
    svc #0

// Branches com sinal (SIGNED):
// b.eq  → Z=1         b.ne  → Z=0
// b.lt  → N≠V         b.ge  → N=V
// b.gt  → Z=0 e N=V   b.le  → Z=1 ou N≠V`
      },
      {
        name: "etapa8b.s", desc: "while: somar 1+2+3+4+5",
        code: `// etapa8b.s — while loop

.section .text
.global _start

_start:
    mov w0, #0          // w0 = soma (acumulador)
    mov w1, #1          // w1 = i (contador começa em 1)

loop:
    cmp w1, #5          // compara i com 5
    b.gt fim            // se i > 5 → sai do loop

    add w0, w0, w1      // soma += i
    add w1, w1, #1      // i++

    b loop              // volta para o início

fim:
    // w0 = 1+2+3+4+5 = 15

    mov x8, #93
    mov x0, #0          // retorna 0 (não o resultado)
    svc #0

// Para retornar o resultado como exit code:
//   mov x0, x0        (já está em w0, que é parte baixa de x0)
// Mas exit codes são de 0–255, então soma 15 funcionaria:
//   mov x0, x0
// e depois: echo $?  → 15`
      }
    ]
  },
  {
    num: "Etapa 9", color: "coral",
    title: "for, switch e jump table",
    tags: ["for", "switch-case", "jump table", "adr", "lsl #3", "AND para paridade"],
    info: [
      { tag: "for",              exp: "Padrão de 4 partes: (1) inicializa registradores; (2) cmp + branch de saída; (3) corpo; (4) incremento + b loop. A condição usa branch de SAÍDA (não de repetição). Equivalente direto a for(init; cond; incr) em C.", code: "// for (i = 0; i < 10; i++) soma += i;\n    mov w0, #0       // soma = 0\n    mov w1, #0       // i = 0\nloop:\n    cmp w1, #10\n    b.ge fim         // i >= 10 → sai\n    add w0, w0, w1   // soma += i\n    add w1, w1, #1   // i++\n    b loop\nfim:" },
      { tag: "switch-case",      exp: "Com poucos cases: cmp + branch em cascata (tempo linear). Com muitos cases densos: jump table (tempo constante). O compilador escolhe automaticamente; em Assembly você implementa manualmente o que for mais adequado.", code: "// switch (x) { case 0,1,2 }\n    cmp x0, #2\n    b.hi default_case   // x > 2 → default\n    adr x1, tabela\n    ldr x2, [x1, x0, lsl #3]\n    br x2               // salta para o case\ncaso0: mov w1, #10 ; b fim\ncaso1: mov w1, #20 ; b fim\ncaso2: mov w1, #40\nfim:" },
      { tag: "jump table",       exp: "Tabela de endereços em .data com .xword (8 bytes por entrada). O índice do case é multiplicado por 8 (lsl #3) e somado ao endereço base da tabela. br xN faz o salto indireto. Requer cases contíguos a partir de 0.", code: ".section .data\ntabela:\n    .xword caso0  // tabela[0] = end. de caso0\n    .xword caso1  // tabela[1] = end. de caso1\n    .xword caso2  // tabela[2] = end. de caso2\n\n// acessar:\n    ldr x2, [x1, x0, lsl #3]  // x0 = índice\n    br x2                       // salto indireto" },
      { tag: "adr",              exp: "adr xN, label carrega o endereço do label relativo ao PC em uma única instrução, sem literal pool. Mais eficiente para labels próximos (±1 MB). Para distâncias maiores, use adrp + add :lo12:.", code: "// adr: uma instrução, para labels próximos\n    adr x1, tabela\n\n// ldr =label: usa literal pool, qualquer distância\n    ldr x1, =tabela\n\n// adrp + add: para endereços muito distantes\n    adrp x1, tabela\n    add  x1, x1, :lo12:tabela" },
      { tag: "lsl #3",           exp: "lsl #N no operando de endereçamento multiplica o índice por 2^N sem instrução extra — codificado diretamente na instrução ldr. lsl #3 = ×8 para .xword (64 bits). lsl #2 = ×4 para .word (32 bits).", code: "// .xword = 8 bytes: lsl #3\nldr x2, [x1, x0, lsl #3]   // x2 = tabela[x0]\n\n// .word = 4 bytes: lsl #2\nldr w3, [x4, x5, lsl #2]   // w3 = vetor[x5]\n\n// Equivalente menos eficiente:\n// mul x6, x0, #8\n// ldr x2, [x1, x6]" },
      { tag: "AND para paridade", exp: "O bit 0 (menos significativo) determina paridade: 0 = par, 1 = ímpar. AND com 1 isola esse bit sem afetar os demais. tst w1, #1 é equivalente e mais direto: faz AND, atualiza flags e descarta resultado.", code: "// Método 1: and + cmp\n    and w2, w1, #1\n    cmp w2, #0\n    b.eq par       // w2=0 → bit0=0 → par\n\n// Método 2: tst (mais direto)\n    tst w1, #1\n    b.eq par       // Z=1 → bit0=0 → par\n    b.ne impar     // Z=0 → bit0=1 → ímpar" },
    ],
    exemplos: [
      {
        name: "etapa9a.s", desc: "for: somar 0+1+...+9",
        code: `// etapa9a.s — for loop (soma 0 a 9)

.global _start
.section .text

_start:
    mov w0, #0          // w0 = soma = 0
    mov w1, #0          // w1 = i = 0

loop_for:
    cmp w1, #10         // compara i com 10
    b.ge fim_for        // se i >= 10 → sai

    add w0, w0, w1      // soma += i
    add w1, w1, #1      // i++
    b loop_for

fim_for:
    // w0 = 0+1+2+...+9 = 45

    mov x8, #93
    mov x0, #0
    svc #0

// Equivalente C:
// for (int i = 0; i < 10; i++) soma += i;
//
// Todo for em Assembly:
// 1. inicialização (mov)
// 2. condição (cmp + branch de saída)
// 3. corpo
// 4. incremento
// 5. branch de retorno`
      },
      {
        name: "etapa9b.s", desc: "switch com jump table (br x2)",
        code: `// etapa9b.s — switch com jump table

.global _start
.section .text

_start:
    mov x0, #1          // entrada: x = 1

    cmp x0, #2
    b.hi default_case   // se x > 2 → default

    adr x1, tabela      // x1 = endereço da tabela
    ldr x2, [x1, x0, lsl #3]  // x2 = tabela[x * 8]
                               // lsl #3 = *8 (cada endereço = 8 bytes)
    br x2               // salto indireto para o caso

caso0: mov w1, #1  ; b fim_switch
caso1: mov w1, #2  ; b fim_switch
caso2: mov w1, #4  ; b fim_switch
default_case: mov w1, #0

fim_switch:
    mov x8, #93
    mov x0, #0
    svc #0

.section .data
tabela:
    .xword caso0        // 8 bytes = endereço de caso0
    .xword caso1
    .xword caso2

// Vantagem: tempo constante para qualquer case
// O compilador usa jump tables para switch com muitos cases`
      },
      {
        name: "etapa9c.s", desc: "somar apenas pares de 0 a 10 com AND",
        code: `// etapa9c.s — somar pares com AND bit a bit

.global _start
.section .text

_start:
    mov w0, #0          // w0 = soma
    mov w1, #0          // w1 = i

loop:
    cmp w1, #11
    b.ge fim

    and w2, w1, #1      // w2 = i & 0b00000001
    cmp w2, #0
    b.ne impar          // se bit 0 = 1 → ímpar, pula

    add w0, w0, w1      // soma += i (só chega aqui se par)

impar:
    add w1, w1, #1
    b loop

fim:
    // w0 = 0+2+4+6+8+10 = 30

    mov x8, #93
    mov x0, #0
    svc #0

// AND com 1 isola o bit menos significativo:
// 4 = 0b100 → AND 1 = 0 → par
// 5 = 0b101 → AND 1 = 1 → ímpar`
      },
      {
        name: "etapa9d.s", desc: "switch com operações por caso",
        code: `// etapa9d.s — switch com jump table + ops

.global _start
.section .text

_start:
    mov x0, #2          // x = 2

    cmp x0, #2
    b.hi default_case

    adr x1, tabela
    ldr x2, [x1, x0, lsl #3]
    br x2

caso0: add x3, x0, #10   // y = x + 10  →  0 + 10 = 10
       b fim

caso1: add x3, x0, x0    // y = x * 2   →  add é mais eficiente
       b fim              //                que mul para *2

caso2: mul x3, x0, x0    // y = x * x   →  2 * 2 = 4
       b fim

default_case:
    mov x3, #-1          // y = -1

fim:
    mov x8, #93
    mov x0, #0
    svc #0

.section .data
tabela:
    .xword caso0
    .xword caso1
    .xword caso2`
      }
    ]
  }
];

export const refContent = `// assembly_aarch64_referencia — Resumo rápido

\u2550\u2550\u2550 COMPILAR \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
gcc -static -nostdlib -g <NOME>.s -o <NOME>
./<NOME>
echo $?    \u2190 código de retorno

\u2550\u2550\u2550 REGISTRADORES \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
x0\u2013x7    argumentos de função / retorno em x0
x8       número da syscall (Linux ARM64)
x9\u2013x18   temporários (caller-saved)
x19\u2013x28  preservados (callee-saved)
x29      frame pointer (FP)
x30      link register (LR) \u2014 endereço de retorno
sp       stack pointer
xzr      zero register (leitura = 0, escrita = descarte)
pc       program counter

wN = parte baixa de xN (32 bits)

\u2550\u2550\u2550 SYSCALLS LINUX ARM64 \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
 1  write(fd, buf, count)   \u2192 x0=fd x1=buf x2=len
 3  read(fd, buf, count)
 4  open(filename, flags)
 5  close(fd)
60  exit_group(code)
93  exit(code)              \u2190 mais comum em exemplos

\u2550\u2550\u2550 DIRETIVAS \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
.global _start    ponto de entrada visível ao linker
.section .text    código executável
.section .data    dados inicializados (leitura/escrita)
.section .bss     dados não-inicializados (zerados)
.word   N         4 bytes (32 bits)
.dword  N         8 bytes (64 bits)
.byte   N         1 byte
.ascii "str"      string sem \\0
.asciz "str"      string com \\0
.double F         double 64 bits (FPU)
.float  F         float  32 bits (FPU)
.skip   N         reserva N bytes em .bss
.equ    SYM, V    constante simbólica (sem memória)
.xword  LABEL     ponteiro 64 bits (para jump tables)

\u2550\u2550\u2550 INSTRUÇÕES PRINCIPAIS \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
mov xd, #imm      xd = imm
mov xd, xn        xd = xn
ldr xd, =label    xd = endereço de label  (pseudo)
ldr xd, [xn]      xd = mem[xn]
ldr xd, [xn, #off] xd = mem[xn + off]
str xs, [xn]      mem[xn] = xs
ldrb wd, [xn]     carrega 1 byte, zero-extends
strb ws, [xn]     armazena 1 byte

add xd, xn, xm    xd = xn + xm
sub xd, xn, xm    xd = xn - xm
mul xd, xn, xm    xd = xn * xm
madd xd, xn, xm, xa   xd = (xn * xm) + xa
sdiv xd, xn, xm   xd = xn / xm (com sinal)
udiv xd, xn, xm   xd = xn / xm (sem sinal)
and xd, xn, xm    xd = xn & xm
orr xd, xn, xm    xd = xn | xm
eor xd, xn, xm    xd = xn ^ xm
lsl xd, xn, #N    xd = xn << N
lsr xd, xn, #N    xd = xn >> N (lógico)
asr xd, xn, #N    xd = xn >> N (aritmético)

cmp xn, xm        atualiza NZCV (= subs xzr, xn, xm)
b   label         desvio incondicional
bl  label         branch with link (salva PC+4 em x30)
ret               volta para x30

b.eq  Z=1    b.ne  Z=0
b.lt  N\u2260V    b.ge  N=V
b.gt  Z=0,N=V  b.le  Z=1 ou N\u2260V
b.hi  C=1,Z=0  b.lo  C=0   (unsigned)

stp xn, xm, [sp, -16]!   push par de regs (pre-index)
ldp xn, xm, [sp],  16    pop  par de regs (post-index)
svc 0                     syscall

\u2550\u2550\u2550 FPU \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
S0\u2013S31   float  32 bits
D0\u2013D31   double 64 bits (mesmo banco que S, V)

ldr d0, [x0]   carrega double da memória
str d0, [x0]   salva double na memória
fadd/fsub/fmul/fdiv  dd, dn, dm
scvtf d0, w1   int \u2192 double
fcvtzs w0, d0  double \u2192 int (trunca)
fcmp  dn, dm   compara floats (atualiza flags)

\u2550\u2550\u2550 GDB RÁPIDO \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
gdb ./prog
break _start / starti
run
stepi (si)       1 instrução
nexti (ni)       1 instrução (sem entrar em funções)
continue (c)
info registers x0 x1 x2 sp pc
p $x0  / p/x $x0 / p/t $x0  (dec/hex/bin)
x/w &var         ler 4 bytes na memória
x/8x $sp         ver topo da stack
disassemble _start
layout regs      modo visual (Ctrl+X A para sair)
display/i $pc    mostrar próxima instrução sempre`;
